import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@appkit4/react-components/modal';
import { Button } from '@appkit4/react-components/button';
import useGenericErrorToast from '../../../../helpers/useGenericErrorToast';
import { AppRoutes } from '../../../../Constants';
import { useGetV1PolicyTermsVersion } from '../../../../api/generated/endpoint';
import useLocale from '../../../../locale/useLocale';
import ModalContainer from '../../../ModalContainer';
import { Checkbox } from '@appkit4/react-components';
import { Dispatch } from 'react';
import { SetStateAction } from 'react';
import './TandCModal.scss';

interface TandCModalProps {
    visible: boolean;
    setVisible: (newValue: boolean) => void;
    setTandcReady: (newValue: boolean) => void;
    setIsGuidanceAccepted: Dispatch<SetStateAction<boolean>>;
}

const TandCModal = ({
    visible,
    setVisible,
    setTandcReady,
    setIsGuidanceAccepted,
}: TandCModalProps) => {
    const navigate = useNavigate();
    const { l } = useLocale();
    const showGenericErrorToast = useGenericErrorToast();

    const [isCheckboxOneChecked, setIsCheckboxOneChecked] = useState<boolean>(
        () => {
            return JSON.parse(sessionStorage.getItem('checkbox1') || 'false');
        }
    );

    const [isCheckboxTwoChecked, setIsCheckboxTwoChecked] = useState<boolean>(
        () => {
            return JSON.parse(sessionStorage.getItem('checkbox2') || 'false');
        }
    );

    useEffect(() => {
        sessionStorage.setItem(
            'checkbox1',
            JSON.stringify(isCheckboxOneChecked)
        );
        sessionStorage.setItem(
            'checkbox2',
            JSON.stringify(isCheckboxTwoChecked)
        );
    }, [isCheckboxOneChecked, isCheckboxTwoChecked]);

    const {
        data: tandCVersion,
        isLoading: loadingTandCVersion,
        isFetchedAfterMount: tandCFetched,
        error: tandCVersionError,
        isSuccess: tandCVersionSuccess,
    } = useGetV1PolicyTermsVersion();

    useEffect(() => {
        if (tandCVersionError) {
            showGenericErrorToast(tandCVersionError);
        }
        if (tandCVersionSuccess) {
            setTandcReady(true);
        }
    }, [tandCVersionError, showGenericErrorToast, tandCVersionSuccess]);

    // if T&Cs has been accepted but cookies haven't then make sure they are shown
    useEffect(() => {
        if (!tandCFetched && !loadingTandCVersion) {
            setTandcReady(true);
        }
    }, [tandCFetched, loadingTandCVersion]);

    const isGuidanceAccepted = isCheckboxOneChecked && isCheckboxTwoChecked;

    const handleDenyTandC = () => {
        navigate(AppRoutes.LogoutPage);
    };

    useEffect(() => {
        if (isGuidanceAccepted) {
            setIsGuidanceAccepted(true);
            setVisible(false);
        }
    }, [setVisible, setIsGuidanceAccepted]);

    return (
        <>
            {visible && (
                <ModalContainer isVisible={visible}>
                    <Modal
                        maskBodyPadding={false}
                        maskCloseable={false}
                        visible={visible}
                        closable={false}
                        initialFocus={false}
                        title={l('_guidanceAndAgreement')}
                        ariaLabel={l('_guidanceAndAgreement')}
                        onCancel={() => handleDenyTandC()}
                        modalStyle={{
                            width: '53.75rem',
                            overflow: 'hidden',
                            maxHeight: '90vh',
                        }}
                        footerStyle={{
                            paddingTop: '8px',
                            marginTop: '8px',
                            minHeight: '64px',
                        }}
                        header={''}
                        icons={''}
                        footer={
                            <>
                                <Button
                                    onClick={() => {
                                        setVisible(false);
                                    }}
                                    disabled={
                                        !(
                                            isCheckboxOneChecked &&
                                            isCheckboxTwoChecked
                                        )
                                    }
                                >
                                    {l('_commonContinue')}
                                </Button>
                            </>
                        }
                        bodyStyle={{ maxHeight: '50vh' }}
                    >
                        <div className="modal-content">
                            <p>When and how to complete this form</p>
                            <br />
                            <p>
                                {l('_guidanceAndAgreementPara1')}
                                <span>
                                    <a
                                        className="guidance-links"
                                        href="https://pwcuk.service-now.com/hub?id=kb_article_view&sysparm_article=KB0015366"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {l('_guidanceAndAgreementPara1Link1')}
                                    </a>
                                </span>
                                <span>{l('_guidanceAndAgreementPara1b')}</span>
                                <span>
                                    <a
                                        className="guidance-links"
                                        href="https://pwcuk.service-now.com/hub?id=kb_article_view&sysparm_article=KB0015021"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {l('_guidanceAndAgreementPara1link2')}
                                    </a>
                                    {'.'}
                                </span>
                                <span>{l('_guidanceAndAgreementPara1d')}</span>
                                <span>
                                    <a
                                        className="guidance-links"
                                        href="https://pwcuk.service-now.com/hub?id=kb_article&table=kb_knowledge&sysparm_article=KB0015022"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {l('_guidanceAndAgreementFAQs')}
                                    </a>
                                    {'.'}
                                </span>
                            </p>
                            <br />
                            <p>
                                {l('_guidanceAndAgreementPara2')}
                                <a
                                    className="guidance-links"
                                    href="https://pwcuk.service-now.com/hub?id=kb_article_view&sysparm_article=KB0015366"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {l('_guidanceAndAgreementPara2Link4')}
                                </a>
                                {'.'}
                            </p>
                            <br />
                            <p>{l('_guidanceAndAgreementPara3')}</p> <br />
                            <span>
                                <u>
                                    <strong>
                                        {l('_guidanceAndAgreementTitle')}
                                    </strong>
                                </u>
                            </span>
                            <span>
                                {l(
                                    '_guidanceAndAgreementDisciplinaryProcedureParaA'
                                )}
                                <a
                                    className="guidance-links"
                                    href="https://pwcuk.service-now.com/hub?id=kb_article_view&sysparm_article=KB0013450"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {l(
                                        '_guidanceAndAgreementDisciplinaryProcedureLink1'
                                    )}
                                </a>
                                {l(
                                    '_guidanceAndAgreementDisciplinaryProcedureParaB'
                                )}

                                {l(
                                    '_guidanceAndAgreementDisciplinaryProcedureParaB2'
                                )}
                            </span>
                            <br />
                            <span>
                                <strong>
                                    <u>{l('_amendmentsTitle')}</u>
                                </strong>
                            </span>
                            <p>{l('_guidanceAndAgreementPara4')}</p>
                            <br />
                            <span>
                                <strong>
                                    <u>{l('DataPrivacyTitle')}</u>
                                </strong>
                            </span>
                            <span>
                                <p>
                                    {l(
                                        '_guidanceAndAgreementSharingPartiesParaA'
                                    )}
                                    <a
                                        className="guidance-links"
                                        href="https://pwcuk.service-now.com/hub?id=kb_article_view&sys_kb_id=0370b81dfbe4a250e0a5fe57aeefdcc1&spa=1"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {l('_guidanceAndAgreementPara6Link')}
                                    </a>
                                    {l(
                                        '_guidanceAndAgreementSharingPartiesParaB'
                                    )}
                                </p>
                            </span>
                            <br />
                            <p> {l('_guidanceAndAgreementPara7')}</p>
                            <br />
                            <Checkbox
                                onChange={(value: boolean) =>
                                    setIsCheckboxOneChecked(value)
                                }
                            >
                                {l('_guidanceAndAgreementCheckbox1')}
                            </Checkbox>
                            <br />
                            <Checkbox
                                onChange={(value: boolean) =>
                                    setIsCheckboxTwoChecked(value)
                                }
                            >
                                <p>
                                    <span>
                                        {l('_guidanceAndAgreementCheckbox2')}
                                    </span>
                                    <a
                                        onClick={(e) => e.stopPropagation()}
                                        className="guidance-links"
                                        href="https://pwcuk.service-now.com/hub?id=kb_article&table=kb_knowledge&sysparm_article=KB0015021"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {l(
                                            '_guidanceAndAgreementCheckbox2Link1'
                                        )}
                                    </a>
                                    <span>
                                        {l('_guidanceAndAgreementCheckbox2a')}
                                    </span>
                                    <a
                                        onClick={(e) => e.stopPropagation()}
                                        className="guidance-links"
                                        href="https://pwcuk.service-now.com/hub?id=kb_article_view&sysparm_article=KB0015366"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {l(
                                            '_guidanceAndAgreementCheckbox2Link2'
                                        )}
                                    </a>
                                    <span>
                                        {l('_guidanceAndAgreementCheckbox2b')}

                                        {l('_guidanceAndAgreementCheckbox2c')}

                                        {':'}
                                    </span>
                                </p>
                                <ul>
                                    <li>
                                        {l(
                                            '_guidanceAndAgreementCheckbox2Bullet1'
                                        )}
                                    </li>
                                    <li>
                                        {l(
                                            '_guidanceAndAgreementCheckbox2Bullet2'
                                        )}
                                    </li>
                                    <li>
                                        {l(
                                            '_guidanceAndAgreementCheckbox2Bullet3'
                                        )}
                                    </li>
                                </ul>
                            </Checkbox>
                            <br />
                        </div>
                        <div className="gradient-fade" />
                    </Modal>
                </ModalContainer>
            )}
        </>
    );
};

export default TandCModal;
